import { marked } from 'marked';
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import type { ReactNode } from 'react';

const TYPING_SPEED = 5;

interface ParagraphProps {
  children?: ReactNode;
  paragraph: string[];
}

interface ParagraphsProps {
  messages: string[];
}

interface TypingEffectProps extends ParagraphsProps {
  setIsTypingFinished: () => void;
}

/**
 * Used for following current row and column in the flow
 */
export const useRowControl = (messages: string[], setIsTypingFinished: () => void): [number[], boolean] => {
  const [rowAndCol, setRowAndCol] = useState<[number, number]>([0, 0]);
  const isLastRow = rowAndCol[0] === messages.length - 1;
  const isLastCol = rowAndCol[1] === messages[rowAndCol[0]].length;
  const msg = messages[rowAndCol[0]];

  useEffect(() => {
    if (isLastRow && isLastCol) {
      return setIsTypingFinished();
    }

    const timeout = setTimeout(() => {
      if (msg.length !== rowAndCol[1]) {
        setRowAndCol(([r, c]) => {
          return [r, c + 1];
        });
      } else {
        setRowAndCol(([r]) => [r + 1, 0]);
      }
      return;
    }, TYPING_SPEED);
    return () => timeout && clearInterval(timeout);
  }, [isLastCol, isLastRow, msg, rowAndCol, setIsTypingFinished]);

  return [rowAndCol, isLastRow && isLastCol];
};

/**
 * Used for creating typing effect that shows one letter at a time: l, lo, lor, lore, lorem, etc.
 */
export const useTypingEffect = (messages: string[], setIsTypingFinished: () => void): [string[][], boolean] => {
  const [rowAndCol, isTypingFinished] = useRowControl(messages, setIsTypingFinished);

  const paragraphs = messages.reduce(
    (acc, cur, index, arr) =>
      index <= rowAndCol[0]
        ? [...acc, [cur.substring(0, rowAndCol[0] === index ? rowAndCol[1] : arr[index].length)]]
        : acc,
    []
  );
  return [paragraphs, isTypingFinished];
};

export const parseMarkdownSafely = (messages: string[]) => {
  return messages.map(message => DOMPurify.sanitize(marked.parse(message, { async: false })));
};

const Paragraph = ({ paragraph }: ParagraphProps) => {
  return (
    <>
      {parseMarkdownSafely(paragraph).map((part: string, index: number) => (
        <span
          key={index}
          // eslint-disable-next-line @typescript-eslint/naming-convention
          dangerouslySetInnerHTML={{ __html: part }}
        ></span>
      ))}
    </>
  );
};

export const Paragraphs = ({ messages }: ParagraphsProps) => {
  const parsedMessages = parseMarkdownSafely(messages);

  return (
    <>
      {parsedMessages.reduce(
        (acc, cur, index) => [...acc, <Paragraph key={index} paragraph={[cur.substring(0, cur.length)]} />],
        []
      )}
    </>
  );
};

export const TypingEffect = ({ messages, setIsTypingFinished }: TypingEffectProps) => {
  const [paragraphs] = useTypingEffect(messages, setIsTypingFinished);

  return (
    <>
      {paragraphs.map((paragraph, i) => (
        <Paragraph key={i} paragraph={paragraph} />
      ))}
    </>
  );
};
